import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ExerciseCardProps, ExerciseSet } from "@/lib/types";
import { ExerciseType } from "@shared/schema";
import { getExerciseImage } from "@/lib/exerciseImages";
import ProgressiveTraining from "./ProgressiveTraining";

const ExerciseCard: React.FC<ExerciseCardProps> = ({
  id,
  workoutId,
  name,
  description,
  sets,
  reps,
  duration,
  type,
  completed,
  onComplete,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Load previously saved rep counts from localStorage
  const loadSavedReps = (): number[] => {
    try {
      // First try to get reps from workoutId+exerciseId key (more specific)
      const savedRepsKey = `workout_${workoutId}_exercise_${id}_reps`;
      const savedReps = localStorage.getItem(savedRepsKey);

      if (savedReps) {
        console.log(
          "Loaded saved reps from localStorage:",
          savedRepsKey,
          savedReps
        );
        return JSON.parse(savedReps);
      }

      // Fall back to the legacy key
      const legacyKey = `exercise_${id}_reps`;
      const legacyReps = localStorage.getItem(legacyKey);
      if (legacyReps) {
        console.log(
          "Loaded legacy reps from localStorage:",
          legacyKey,
          legacyReps
        );
        return JSON.parse(legacyReps);
      }
    } catch (error) {
      console.error("Error loading saved reps", error);
    }

    // Return default values based on target reps
    return type === ExerciseType.REPS ? Array(sets).fill(reps || 0) : [];
  };

  const savedRepCounts = loadSavedReps();

  // Load previously saved exercise sets from localStorage
  const loadSavedExerciseSets = (): ExerciseSet[] => {
    try {
      // Try to get saved sets data specific to this workout+exercise
      const savedSetsKey = `workout_${workoutId}_exercise_${id}_sets`;
      const savedSetsString = localStorage.getItem(savedSetsKey);

      if (savedSetsString) {
        const savedSets = JSON.parse(savedSetsString);
        console.log(
          "Loaded saved exercise sets from localStorage:",
          savedSetsKey,
          savedSets
        );

        // Map the saved data to our ExerciseSet structure
        return savedSets.map((savedSet: any, index: number) => ({
          setNumber: index + 1,
          targetReps: type === ExerciseType.REPS ? reps : undefined,
          targetDuration: type === ExerciseType.DURATION ? duration : undefined,
          actualReps: savedSet.actualReps,
          actualDuration: savedSet.actualDuration,
          completed: savedSet.completed || false,
        }));
      }
    } catch (error) {
      console.error("Error loading saved exercise sets", error);
    }

    // If no saved sets or error, create default sets
    return Array.from({ length: sets }, (_, i) => ({
      setNumber: i + 1,
      targetReps: type === ExerciseType.REPS ? reps : undefined,
      targetDuration: type === ExerciseType.DURATION ? duration : undefined,
      actualReps:
        type === ExerciseType.REPS
          ? // Only use the initial rep value if savedRepCounts doesn't have this index
            i < savedRepCounts.length
            ? savedRepCounts[i]
            : reps
          : undefined,
      actualDuration: type === ExerciseType.DURATION ? duration : undefined,
      completed: false,
    }));
  };

  const [exerciseSets, setExerciseSets] = useState<ExerciseSet[]>(
    loadSavedExerciseSets()
  );

  const toggleDetails = () => {
    setIsOpen(!isOpen);
  };

  const handleMarkComplete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onComplete(!completed, exerciseSets);
  };

  const handleSetChange = (setIndex: number, value: number) => {
    const newSets = [...exerciseSets];

    if (type === ExerciseType.REPS) {
      newSets[setIndex].actualReps = value;
    } else {
      newSets[setIndex].actualDuration = value;
    }

    setExerciseSets(newSets);
  };

  const handleSetCompleted = (setIndex: number, completed: boolean) => {
    const newSets = [...exerciseSets];
    newSets[setIndex].completed = completed;
    setExerciseSets(newSets);

    // If all sets are complete, mark the exercise as complete
    if (newSets.every((set) => set.completed)) {
      onComplete(true, newSets);
    }
  };

  const handleSaveProgress = () => {
    // Save the current rep counts to localStorage
    try {
      // Save to both workoutId+exerciseId key and legacy key
      const newSetsData = exerciseSets.map((set) => ({
        setNumber: set.setNumber,
        actualReps: set.actualReps,
        actualDuration: set.actualDuration,
        completed: set.completed,
      }));

      // Save specifically for this workout+exercise combination
      const specificKey = `workout_${workoutId}_exercise_${id}_sets`;
      localStorage.setItem(specificKey, JSON.stringify(newSetsData));

      // Also update the standard reps tracking
      if (type === ExerciseType.REPS) {
        const repCounts = exerciseSets.map((set) => set.actualReps || 0);
        const repsKey = `workout_${workoutId}_exercise_${id}_reps`;
        localStorage.setItem(repsKey, JSON.stringify(repCounts));
      }

      console.log("Saved exercise progress to localStorage");
    } catch (error) {
      console.error("Error saving exercise progress", error);
    }

    // Mark the exercise as complete and pass the exercise sets data
    onComplete(true, exerciseSets);
    setIsOpen(false);
  };

  // Get the appropriate exercise image based on the exercise name
  const exerciseImage = getExerciseImage(name);

  // Fallback image in case the exercise image doesn't load - using pushups as default
  const fallbackImage = "/pushup.png";

  // Track image loading state
  const [imageLoaded, setImageLoaded] = useState(true);

  return (
    <div
      className={`bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden ${
        completed ? "exercise-complete" : ""
      } ${disabled ? "opacity-75" : ""}`}
    >
      <div
        className={`flex p-3 ${disabled ? "cursor-default" : "cursor-pointer"}`}
        onClick={disabled ? undefined : toggleDetails}
      >
        <div className="flex-grow">
          <h3 className="font-heading font-semibold">{name}</h3>
          <div className="flex mt-1">
            <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-medium">
              {sets} sets
            </span>
            <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-medium ml-2">
              {type === ExerciseType.REPS ? `${reps} reps` : `${duration} sec`}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div
            className={`w-6 h-6 rounded-full border-2 ${
              completed ? "border-primary bg-primary" : "border-gray-300"
            } flex items-center justify-center ${
              disabled ? "cursor-default opacity-50" : "cursor-pointer"
            }`}
            onClick={disabled ? undefined : handleMarkComplete}
          >
            {completed && (
              <span className="material-icons text-white text-sm">check</span>
            )}
          </div>
          <span className="material-icons text-gray-400">
            {isOpen ? "expand_less" : "expand_more"}
          </span>
        </div>
      </div>

      {isOpen && (
        <div className="p-3 pt-0 border-t border-gray-200">
          {description && (
            <div className="bg-gray-50 rounded-lg p-3 mb-3">
              <p className="text-sm text-gray-600">{description}</p>
            </div>
          )}

          <div className="flex flex-wrap gap-2 mb-4">
            <div className="relative w-full h-40 bg-gray-50 rounded-lg overflow-hidden">
              {!imageLoaded && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-gray-400">Loading image...</span>
                </div>
              )}
              <img
                src={exerciseImage}
                alt={`${name} demonstration`}
                className="w-full h-full object-cover"
                onLoad={() => setImageLoaded(true)}
                onError={(e) => {
                  // If the image fails to load, use the fallback image
                  e.currentTarget.src = fallbackImage;
                  setImageLoaded(true);
                }}
              />
              <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 text-center">
                {name}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 mb-3">
            {exerciseSets.map((set, index) => (
              <div
                key={index}
                className="bg-gray-50 p-2 rounded-lg flex flex-col items-center space-y-1"
              >
                <span className="text-sm font-medium">Set {set.setNumber}</span>
                <Input
                  type="number"
                  value={
                    type === ExerciseType.REPS
                      ? set.actualReps
                      : set.actualDuration
                  }
                  min="0"
                  className="w-16 text-center"
                  onChange={(e) =>
                    handleSetChange(index, parseInt(e.target.value) || 0)
                  }
                  disabled={disabled}
                />
              </div>
            ))}
          </div>

          {/* Progressive Training Component */}
          <ProgressiveTraining
            sets={sets}
            reps={type === ExerciseType.REPS ? reps : undefined}
            duration={type === ExerciseType.DURATION ? duration : undefined}
            onSetCompleted={handleSetCompleted}
            exerciseSets={exerciseSets}
            disabled={disabled}
          />

          <Button
            className="w-full mt-3 bg-primary text-white"
            onClick={handleSaveProgress}
            disabled={disabled}
          >
            Save Progress
          </Button>
        </div>
      )}
    </div>
  );
};

export default ExerciseCard;
